import { useEffect, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import LeadForm from "./LeadForm";

const MIN_GCI = 60000;
const MAX_GCI = 500000;
const STEP = 5000;
const BEFORE_SPLIT = 75;
const AFTER_SPLIT = 85;
const MONTHLY_FEE = 250;
const ANNUAL_FEE = MONTHLY_FEE * 12;
const GAP = (AFTER_SPLIT - BEFORE_SPLIT) / 100;

const fmt = (v) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(v);

function Ticker({ value, testId }) {
  const mv = useMotionValue(value);
  const spring = useSpring(mv, { stiffness: 140, damping: 22 });
  const text = useTransform(spring, (v) => fmt(Math.round(v / 100) * 100));
  useEffect(() => {
    mv.set(value);
  }, [value, mv]);
  return <motion.span data-testid={testId}>{text}</motion.span>;
}

export default function Calculator() {
  const [gci, setGci] = useState(200000);
  const [formOpen, setFormOpen] = useState(false);

  const extraYear1 = Math.max(gci * GAP - ANNUAL_FEE, 0);
  const extra5yr = extraYear1 * 5;
  const fill = ((gci - MIN_GCI) / (MAX_GCI - MIN_GCI)) * 100;

  return (
    <div
      data-testid="calculator-card"
      className="bg-white border border-[#E5E5E0] shadow-[0_24px_60px_-24px_rgba(17,17,17,0.25)] p-6 sm:p-8"
    >
      <p className="text-[11px] uppercase tracking-[0.3em] text-[#555] mb-5">What staying costs you</p>

      <label htmlFor="gci-slider" className="block text-sm font-semibold mb-1">
        Your annual commission
      </label>
      <div className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mb-5" data-testid="gci-value">
        {fmt(gci)}
      </div>

      <input
        id="gci-slider"
        data-testid="gci-slider"
        type="range"
        min={MIN_GCI}
        max={MAX_GCI}
        step={STEP}
        value={gci}
        style={{ "--fill": `${fill}%` }}
        onChange={(e) => {
          setGci(Number(e.target.value));
          setFormOpen(true);
        }}
        className="gci-range"
        aria-label="Annual commission"
      />
      <div className="flex justify-between text-[11px] text-[#555] mt-2 mb-5">
        <span>$60k</span>
        <span>$500k</span>
      </div>

      <p className="text-xs text-[#555] mb-6" data-testid="split-line">
        Big franchise split: <strong className="text-[#111111]">{BEFORE_SPLIT}%</strong>
        <span className="mx-2 text-[#FF3B30]">→</span>
        Agency HQ split: <strong className="text-[#111111]">{AFTER_SPLIT}%</strong>
      </p>

      <div className="border-t border-[#E5E5E0] pt-5">
        <p className="text-sm text-[#555] mb-1">Extra in your pocket, year one</p>
        <div
          className="font-display font-semibold tracking-tight text-4xl sm:text-5xl text-[#FF3B30]"
          data-testid="calculator-result-year1"
        >
          <Ticker value={extraYear1} testId="extra-year1-value" />
        </div>
        <p className="text-xs text-[#555] mt-2" data-testid="result-caption">
          {AFTER_SPLIT - BEFORE_SPLIT} extra cents on every commission dollar, less Agency HQ's ${MONTHLY_FEE}/month
          fee — no change in how you work.
        </p>
      </div>

      <div className="mt-6" data-testid="five-year-bars">
        <div className="flex items-end gap-2.5 h-[120px]">
          {[1, 2, 3, 4, 5].map((yr) => {
            const cumulative = extraYear1 * yr;
            const heightPct = extraYear1 > 0 ? (yr / 5) * 100 : 0;
            return (
              <div key={yr} className="flex-1 flex flex-col items-center justify-end h-full">
                <span className="text-[10px] font-semibold text-[#FF3B30] mb-1.5" data-testid={`bar-val-yr${yr}`}>
                  {fmt(cumulative)}
                </span>
                <motion.div
                  className="w-full max-w-[46px] bg-[#111111]"
                  animate={{ height: `${heightPct}%` }}
                  transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
                  style={{ minHeight: heightPct > 0 ? 4 : 0 }}
                />
                <span className="text-[10px] text-[#555] mt-1.5">Yr {yr}</span>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-[#999] mt-2">Cumulative extra take-home, 5 years</p>
      </div>

      <p className="text-[10px] leading-relaxed text-[#999] mt-5 border-t border-[#E5E5E0] pt-4" data-testid="calc-footnote">
        Based on a flat {AFTER_SPLIT - BEFORE_SPLIT}-point split increase ({BEFORE_SPLIT}% → {AFTER_SPLIT}%) applied to
        your commission income each year, minus Agency HQ's ${MONTHLY_FEE}/month membership fee ($
        {ANNUAL_FEE.toLocaleString("en-US")}/year), assuming earnings hold steady. Real earnings vary with production,
        fees and market conditions — this is a directional illustration, not a guarantee.
      </p>

      <AnimatePresence>
        {formOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-[#E5E5E0] mt-6 pt-6">
              <p className="text-sm font-semibold mb-4" data-testid="breakdown-prompt">
                Want your full personalised breakdown? We'll send it through.
              </p>
              <LeadForm
                source="calculator"
                commission={gci}
                extraYear1={extraYear1}
                extra5yr={extra5yr}
                testIdPrefix="calc"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!formOpen && (
        <button
          type="button"
          data-testid="reveal-breakdown-button"
          onClick={() => setFormOpen(true)}
          className="mt-6 w-full bg-[#FF3B30] text-white text-sm font-bold uppercase tracking-[0.15em] py-4 transition-[background-color,transform] duration-200 hover:bg-[#E0352B] hover:-translate-y-0.5"
        >
          Get my full breakdown
        </button>
      )}
    </div>
  );
}
